import { FinancePaymentDashboardPeriods, ProviderFinancePeriod, ProviderFinancePeriodItem, ProviderFinancePeriodItemType } from '@uplift/api';
import { FinanceMonthPeriodFilter } from './types';
import { CalendarDateService } from '@uplift/services';


type FinanceMonthCsvRow = {
  submitted: string;
  encounterDate: string;
  payment: number;
  client: string;
  clientState: string;
  cptCodes: string;
};


type FinanceMonthCsvHeader = {
  label: string;
  key: keyof FinanceMonthCsvRow;
};

export const getEncounterDate = (item: ProviderFinancePeriodItem) => {
  switch (item.itemType) {
    case ProviderFinancePeriodItemType.cancelledSession:
    case ProviderFinancePeriodItemType.completedSession: {
      return item.appointment.fromDatetime;
    }
    case ProviderFinancePeriodItemType.billableClinicalNote: {
      const encounterDateField = item.note.form.fields.find(f => f.id === 'communication_date');
      const encounterDate = encounterDateField.fieldType === 'date' ? encounterDateField.value : null;

      if (encounterDate) {
        return encounterDate;
      }

      return item.note.submittedAt;
    }
  }
  return null;
};

export const convertFinanceMonthRowsToCsvData = (rows: ProviderFinancePeriodItem[]) => {
  const csvData = rows.map((row): FinanceMonthCsvRow => {
    const encounterDate = CalendarDateService.date(getEncounterDate(row));

    switch (row.itemType) {
      case ProviderFinancePeriodItemType.cancelledSession:
      case ProviderFinancePeriodItemType.completedSession: {
        const submitted = CalendarDateService.date(row.submittedAt);

        return {
          payment: row.payoutAmount,
          submitted: submitted.format('MM/DD/YYYY H:mm A'),
          encounterDate: encounterDate.format('MM/DD/YYYY H:mm A'),
          clientState: row.addressState || '',
          client: row.patient ? `${row.patient.firstName} ${row.patient.lastName}` : '',
          cptCodes: row.cptCodes?.map(c => c.code).join(', '),
        };
      }
      case ProviderFinancePeriodItemType.billableClinicalNote: {
        const submitted = CalendarDateService.date(row.submittedAt);

        return {
          payment: row.payoutAmount,
          submitted: submitted.format('MM/DD/YYYY H:mm A'),
          encounterDate: encounterDate?.format('MM/DD/YYYY'),
          clientState: row.addressState || '',
          client: row.patient ? `${row.patient.firstName} ${row.patient.lastName}` : '',
          cptCodes: row.cptCodes?.map(c => c.code).join(', '),
        };
      }
    }
    return null;
  });

  const csvHeaders: FinanceMonthCsvHeader[] = [
    { label: 'Submitted', key: 'submitted' },
    { label: 'Encounter Date', key: 'encounterDate' },
    { label: 'Client', key: 'client' },
    { label: 'Client State', key: 'clientState' },
    { label: 'CPT codes', key: 'cptCodes' },
    { label: 'Payment', key: 'payment' },
  ];

  return { csvData, csvHeaders };
};


const DEFAULT_PERIOD: ProviderFinancePeriod = {
  started: '',
  ended: '',
  payout: 0,
  sessions: 0,
  paid: 0,
  lateFees: 0,
  items: [],
};


export const getCurrentPeriod = (periodsResult: FinancePaymentDashboardPeriods, filter: FinanceMonthPeriodFilter): ProviderFinancePeriod => {
  if (!periodsResult) {
    return DEFAULT_PERIOD;
  }

  switch (filter) {
    case FinanceMonthPeriodFilter.first:
      return periodsResult.last;
    case FinanceMonthPeriodFilter.second:
      return periodsResult.this;
    case FinanceMonthPeriodFilter.all:
      return {
        started: periodsResult.last.started,
        ended: periodsResult.this.ended,
        payout: (periodsResult.last.payout || 0) + (periodsResult.this.payout || 0),
        sessions: (periodsResult.last.sessions || 0) + (periodsResult.this.sessions || 0),
        paid: periodsResult.paid,
        lateFees: (periodsResult.last.lateFees || 0) + (periodsResult.this.lateFees || 0),
        items: [ ...periodsResult.last.items, ...periodsResult.this.items, ],
      };
    default:
      return DEFAULT_PERIOD;
  }
};
